import axios from "axios";
import { rootStore } from "@/mobx/store";
import {
  API_URL,
  API_URL_DEV,
  API_URL_LOCAL,
  HEADER,
  RESULT_CODE,
} from "@/config/const";
import { refreshAccessToken, isTokenExpiringSoon } from "@/utils/tokenRefresh";

// API URL 결정
let apiUrl = `${API_URL}`;
const currentUrl = window.location.href;

if (currentUrl.includes("dev.chewingpay.com")) {
  apiUrl = `${API_URL_DEV}`;
} else if (currentUrl.includes("chewingpay.com")) {
  apiUrl = `${API_URL}`;
} else if (currentUrl.includes("localhost")) {
  apiUrl = `${API_URL_LOCAL}`;
}

// 글로벌 로딩 상태 관리를 위한 변수
let activeRequests = 0;
const loadingSubscribers = new Set<(loading: boolean) => void>();

// 로딩 상태 변경 알림 함수
const notifyLoadingChange = (loading: boolean) => {
  loadingSubscribers.forEach((callback) => callback(loading));
};

// 로딩 상태 구독 함수
export const subscribeToLoading = (callback: (loading: boolean) => void) => {
  loadingSubscribers.add(callback);
  return () => loadingSubscribers.delete(callback);
};

// axios 인스턴스 생성
const api = axios.create({
  baseURL: apiUrl,
  headers: {
    [HEADER.LANG]: "ko", // 언어 설정
  },
});

// 토큰 갱신 중복 방지 플래그
let isRefreshing = false;

// 요청 전: 토큰 갱신 및 헤더에 추가
api.interceptors.request.use(async (config) => {
  // 로딩 상태 증가
  activeRequests++;
  notifyLoadingChange(true);

  try {
    // refresh endpoint가 아닌 경우에만 토큰 갱신 시도
    if (!config.url?.includes("/office/reissueToken")) {
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        const currentToken = rootStore.token;

        // 토큰이 없거나 곧 만료될 예정이면 갱신
        if (!currentToken || isTokenExpiringSoon(currentToken)) {
          // 이미 갱신 중이면 기존 토큰 사용 (중복 갱신 방지)
          if (isRefreshing) {
            if (currentToken) {
              config.headers[HEADER.AUTH_TOKEN] = currentToken;
              config.headers["Authorization"] = `Bearer ${currentToken}`;
            }
          } else {
            isRefreshing = true;
            try {
              const newToken = await refreshAccessToken();
              isRefreshing = false;
              config.headers[HEADER.AUTH_TOKEN] = newToken;
              config.headers["Authorization"] = `Bearer ${newToken}`;
            } catch (error) {
              isRefreshing = false;
              console.error(
                "[REQUEST INTERCEPTOR] Token refresh failed:",
                error
              );
              // 갱신 실패 시 기존 토큰 사용 (401이 오면 response interceptor에서 처리)
              if (currentToken) {
                config.headers[HEADER.AUTH_TOKEN] = currentToken;
                config.headers["Authorization"] = `Bearer ${currentToken}`;
              }
            }
          }
        } else {
          // 기존 토큰 사용
          config.headers[HEADER.AUTH_TOKEN] = currentToken;
          config.headers["Authorization"] = `Bearer ${currentToken}`;
        }
      } else {
        // 기존 토큰 사용
        const token = rootStore.token;
        if (token) {
          config.headers[HEADER.AUTH_TOKEN] = token;
          config.headers["Authorization"] = `Bearer ${token}`;
        }
      }
    }
  } catch (error) {
    console.error("[REQUEST INTERCEPTOR] Error:", error);
    // 토큰 갱신 실패 시 기존 토큰 사용
    const token = rootStore.token;
    if (token) {
      config.headers[HEADER.AUTH_TOKEN] = token;
      config.headers["Authorization"] = `Bearer ${token}`;
    }
  }

  return config;
});

// 응답 에러 처리 (토큰 만료 대응)
api.interceptors.response.use(
  (res) => {
    // 로딩 상태 감소
    activeRequests = Math.max(0, activeRequests - 1);

    if (activeRequests === 0) {
      notifyLoadingChange(false);
    }
    return res;
  },
  async (err) => {
    // 로딩 상태 감소
    activeRequests = Math.max(0, activeRequests - 1);

    if (activeRequests === 0) {
      notifyLoadingChange(false);
    }

    const originalRequest = err.config;

    // 서버가 Spring Security 기본 401 을 반환하는 경우 (body 에 result_code 없음)
    // 와 전통적인 result_code:-2 포맷 모두를 토큰 만료로 간주해 refresh 플로우로 위임한다.
    // 단, /office/reissueToken 자체의 실패는 무한 루프 방지를 위해 제외.
    const isRefreshEndpoint = originalRequest.url?.includes(
      "/office/reissueToken"
    );
    const isTokenExpired =
      !isRefreshEndpoint &&
      (err.response?.data?.result_code === RESULT_CODE.TOKEN_EXPIRED ||
        err.response?.status === 401);

    // 토큰 만료 시 재요청 로직
    if (isTokenExpired && !originalRequest._retry) {
      originalRequest._retry = true;

      // 이미 갱신 중이면 잠시 대기 후 재시도
      if (isRefreshing) {
        // 최대 5초 대기
        await new Promise((resolve) => setTimeout(resolve, 5000));
        const newToken = rootStore.token;
        if (newToken) {
          originalRequest.headers[HEADER.AUTH_TOKEN] = newToken;
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      }

      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        originalRequest.headers[HEADER.AUTH_TOKEN] = newToken;
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (e) {
        isRefreshing = false;
        // refresh 실패 시 로그아웃 처리
        rootStore.signOut();
        if (window.location.pathname !== "/login") {
          window.location.href = "/login?reason=token_refresh_failed";
        }
        return Promise.reject(e);
      }
    }

    // 인증 관련 에러 처리 (토큰이 없는 경우만)
    if (err.response?.data?.result_code === RESULT_CODE.EMPTY_TOKEN) {
      rootStore.signOut();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login?reason=empty_token";
      }
      return Promise.reject(err);
    }

    return Promise.reject(err);
  }
);

export default api;
